import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useT } from "../i18n";
import { useShell } from "../../features/shell/useShell";
import { CONTENT_MAX_WIDTH, PHONE_FOOTER_HEIGHT } from "../../features/shell/layout";

/** Height reserved at the end of a bulk list so the fixed bar does not cover the last row. */
export const BULK_MOVE_BAR_SPACER = 72;

/** Snackbar lift above the fixed bar (bar height plus breathing room). */
export const BULK_MOVE_SNACKBAR_OFFSET = 88;

interface BulkMoveBarProps {
  count: number;
  onMove: () => void;
}

/**
 * Screen-wide, square-cornered bar shared by both bulk modes (bulk-move 05/06,
 * spec "Sticky bar"). It carries only the selected count as primary-coloured
 * text and the move action: cancelling stays on the header's Select → Cancel
 * selection toggle. Controls stay centred in the content column while the bar
 * spans the viewport; the phone shell keeps it above the persistent footer.
 */
export function BulkMoveBar({ count, onMove }: BulkMoveBarProps) {
  const t = useT();
  const shell = useShell();
  return (
    <Paper
      elevation={6}
      square
      data-testid="bulk-move-bar"
      sx={{
        position: "fixed",
        left: 0,
        right: 0,
        bottom: shell === "phone" ? `calc(${PHONE_FOOTER_HEIGHT}px + env(safe-area-inset-bottom))` : 0,
        zIndex: (theme) => theme.zIndex.modal - 1,
        borderRadius: 0,
        borderTop: 1,
        borderColor: "divider",
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{ maxWidth: CONTENT_MAX_WIDTH, mx: "auto", p: 1, px: { xs: 2, md: 3, lg: 4 } }}
      >
        <Typography
          variant="body2"
          data-testid="bulk-move-count"
          sx={{ color: "primary.main", fontWeight: 650 }}
        >
          {t("bulkSelected", { count: String(count) })}
        </Typography>
        <Button
          size="small"
          variant="contained"
          onClick={onMove}
          disabled={count === 0}
          data-testid="bulk-move-action"
        >
          {t("bulkMoveAction")}
        </Button>
      </Stack>
    </Paper>
  );
}
